import type { ReactNode } from 'react';
import { Sparkles, Archive, MonitorSmartphone, BellRing, HeartPulse, ShieldCheck, WifiOff, KeyRound, Globe } from 'lucide-react';
import { Button, Container, Eyebrow, IconTile, Reveal, SectionHeading, CheckList } from '@/components/ui';
import { CtaBand, PageHero } from '@/components/sections';
import { AlertsMockup, FleetMockup, ModuleVisual, DashboardMockup } from '@/components/mockups';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { CLOUD_PLANS, type CloudPlanCategory } from '@scada-ink/shared/cloud';

const fromPrice = (category: CloudPlanCategory) => Math.min(...CLOUD_PLANS.filter((p) => p.category === category).map((p) => p.priceMonthlyUsd));

function Service({
  id,
  icon,
  color,
  eyebrow,
  title,
  body,
  points,
  visual,
  price,
  reverse,
}: {
  id: string;
  icon: React.ComponentType<{ size?: number }>;
  color: string;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  visual: ReactNode;
  price: string;
  reverse?: boolean;
}) {
  return (
    <section id={id} className="scroll-mt-32 py-16 lg:py-20 border-b border-glass-border last:border-b-0">
      <Container>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className={reverse ? 'lg:order-2' : ''}>
            <div className="flex items-center gap-3">
              <IconTile icon={icon} color={color} />
              <Eyebrow>{eyebrow}</Eyebrow>
            </div>
            <h2 className="mt-5 text-3xl font-bold tracking-tight">{title}</h2>
            <p className="mt-4 text-lg text-text-secondary leading-relaxed">{body}</p>
            <div className="mt-6">
              <CheckList items={points} color={color} />
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button to={`/pricing#${id === 'fleet' ? 'cloud' : id}`} arrow>
                {price}
              </Button>
              <Button to="/register" variant="secondary">
                Subscribe in the portal
              </Button>
            </div>
          </Reveal>
          <Reveal delay={120} className={reverse ? 'lg:order-1' : ''}>
            {visual}
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export function CloudPage() {
  usePageTitle('Scada.Ink Cloud');
  return (
    <>
      <PageHero
        dark
        crumbs={[{ label: 'Cloud' }]}
        eyebrow="Scada.Ink Cloud"
        title="Cloud services that respect the edge"
        intro="The appliance runs without the internet, always. Scada.Ink Cloud is what you add when a connection is available: AI, an off-site archive, remote viewing, SMS and voice alerts, and fleet health — billed monthly and managed from one customer portal."
        actions={
          <>
            <Button to="/pricing#cloud" size="lg" variant="light" arrow>
              Compare plans
            </Button>
            <Button to="/register" size="lg" variant="outline-light">
              Create an account
            </Button>
          </>
        }
        visual={<FleetMockup />}
      />

      <section className="bg-white border-b border-glass-border">
        <Container className="py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: WifiOff, title: 'Optional, never required', body: 'Every on-appliance feature works with the cable unplugged.' },
              { icon: Globe, title: 'Air-gap friendly AI', body: 'AI requests travel through the engineer’s browser, not the plant network.' },
              { icon: KeyRound, title: 'Opt-in health reporting', body: 'Appliances report health only after an admin turns it on.' },
              { icon: ShieldCheck, title: 'No tag values in reports', body: 'Health reports carry counts and metrics — never process data.' },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <f.icon size={20} className="text-brand-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold">{f.title}</div>
                  <div className="text-xs text-text-secondary mt-1 leading-relaxed">{f.body}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <Service
        id="ai"
        icon={Sparkles}
        color="#8b5cf6"
        eyebrow="Smart Assist AI"
        title="Commission machines in minutes, not days"
        body="Smart Assist classifies scanned tags, suggests alarm limits and deadbands from real data, and designs custom pages from a description."
        points={['Tag classification and automatic dashboards', 'AI custom page designer', 'Observation analysis with alarm & deadband suggestions', 'Works on air-gapped appliances']}
        visual={<ModuleVisual visual="ai" />}
        price={`From $${fromPrice('ai')}/month`}
      />
      <Service
        id="archive"
        reverse
        icon={Archive}
        color="#16a34a"
        eyebrow="Cloud Data Archive"
        title="An off-site copy of every value you record"
        body="Keep the appliance lean and still answer the question from three years ago. Archive retains historian data off-site, per appliance, for 13 months or 7 years."
        points={['13-month or 7-year retention', 'CSV export of any tag and time range', 'Resumes after outages — nothing is lost', 'Choose which appliances are archived in the portal']}
        visual={<ModuleVisual visual="analytics" />}
        price={`From $${fromPrice('archive')}/appliance/month`}
      />
      <Service
        id="remote"
        icon={MonitorSmartphone}
        color="#3b82f6"
        eyebrow="Remote Viewing"
        title="The plant floor, from the office or the road"
        body="Give managers and on-call engineers read-only access to dashboards, custom pages and reports without a VPN or an inbound firewall rule."
        points={['Dashboards, custom pages and reports', 'Named viewers managed in the portal', 'Read-only — nothing can be changed remotely', 'No inbound ports opened on the plant network']}
        visual={<DashboardMockup />}
        price={`From $${fromPrice('remote')}/appliance/month`}
      />
      <Service
        id="alerts"
        reverse
        icon={BellRing}
        color="#dc2626"
        eyebrow="SMS & Voice Alerts"
        title="Alarms that wake the right person at 2 a.m."
        body="Alarm pipelines send texts and place phone calls through Twilio. Use our managed number with a monthly allowance, or bring your own Twilio account and caller ID."
        points={['SMS and text-to-speech voice calls', 'Managed number or bring your own Twilio account', 'Recipient allow-list and monthly caps', 'Delivery log with per-appliance attribution']}
        visual={<AlertsMockup />}
        price={`From $${fromPrice('notifications')}/month`}
      />
      <Service
        id="fleet"
        icon={HeartPulse}
        color="#06b6d4"
        eyebrow="Fleet Health — included"
        title="Every appliance’s health, from anywhere"
        body="Internet-connected appliances report CPU, memory, disk, temperature, device connectivity, active alarms and service status. The portal flags anything that needs attention."
        points={['Online, stale and offline status at a glance', 'Warnings for disk, memory, temperature and faulted devices', '24-hour health history per appliance', 'Free for every registered appliance']}
        visual={<FleetMockup />}
        price="Free with any account"
      />

      <section className="py-16 bg-white border-t border-glass-border">
        <Container>
          <SectionHeading
            eyebrow="Plans"
            title="Take one service, or bundle them all"
            intro="Every service can be subscribed to on its own, or together in a bundle for one appliance. Plans, allowances and prices are on the pricing page."
            align="center"
            action={
              <Button to="/pricing#cloud" size="lg" arrow>
                See cloud pricing
              </Button>
            }
          />
        </Container>
      </section>

      <CtaBand title="Not sure which plan fits?" body="Tell us how many appliances and sites you run and we will recommend the right mix of cloud services." />
    </>
  );
}
